import React, { useContext, useState, useEffect, useRef } from "react";
import { auth, firestore } from "../firebase";
import { getIdTokenResult, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { recordClientLogin } from "../services/authAdminService";

const AuthContext = React.createContext();
export function useAuth() {
  return useContext(AuthContext);
}

const STAFF_ROLES = ["admin", "partner", "accounts", "staff"];
const CLIENT_ACCESS_STATUSES = ["invited", "active"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginRecordedFor = useRef(null);

  useEffect(() => {
    let unsubscribeProfile = null;
    let authGeneration = 0;

    const clearProfileListener = () => {
      if (unsubscribeProfile) unsubscribeProfile();
      unsubscribeProfile = null;
    };

    const setUnauthorizedProfile = (firebaseUser) => {
      setUserData({
        id: firebaseUser.uid,
        profileType: "unknown",
        role: "unauthorized",
        active: false,
        name: firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "User",
        email: firebaseUser.email,
      });
      setLoading(false);
    };

    const subscribeToClientProfile = (firebaseUser, fallbackToStaff = false) => {
      clearProfileListener();
      const clientRef = doc(firestore, "clientUsers", firebaseUser.uid);
      unsubscribeProfile = onSnapshot(
        clientRef,
        (clientSnap) => {
          if (!clientSnap.exists()) {
            if (fallbackToStaff) {
              subscribeToStaffProfile(firebaseUser, false);
            } else {
              setUnauthorizedProfile(firebaseUser);
            }
            return;
          }

          const data = clientSnap.data();
          const accessStatus = String(data.accessStatus || "active").toLowerCase();
          // v3.6.3 client profiles did not yet contain portalEnabled. Treat a missing
          // field as a legacy enabled profile only during migration; an explicit false
          // always blocks access. The Cloud Function normalizes legacy/invited profiles
          // to portalEnabled:true asynchronously on successful login.
          const portalEnabled = data.portalEnabled !== false;
          const eligible = data.disabled !== true && portalEnabled && CLIENT_ACCESS_STATUSES.includes(accessStatus);
          const active = eligible;
          setUserData({
            id: firebaseUser.uid,
            profileType: "client",
            role: active ? "client" : "disabled",
            active,
            portalEnabled: data.portalEnabled === true,
            accessStatus,
            name: data.name || data.clientName || firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "Client",
            clientName: data.clientName || data.name || "Client",
            clientId: data.clientId,
            email: data.email || firebaseUser.email,
            ...data,
          });
          setLoading(false);

          // Record/normalize one client login per Auth session without blocking portal
          // rendering. This is important when the frontend is tested locally before the
          // matching Functions deployment has been completed.
          if (active && loginRecordedFor.current !== firebaseUser.uid) {
            loginRecordedFor.current = firebaseUser.uid;
            recordClientLogin().catch((error) => {
              loginRecordedFor.current = null;
              console.warn("Unable to record/normalize client login:", error);
            });
          }
        },
        () => {
          // A staff user is correctly denied access to clientUsers unless their role allows it.
          // For legacy accounts without custom claims, try the staff profile next.
          if (fallbackToStaff) {
            subscribeToStaffProfile(firebaseUser, false);
          } else {
            setUnauthorizedProfile(firebaseUser);
          }
        }
      );
    };

    const subscribeToStaffProfile = (firebaseUser, fallbackToClient = false) => {
      clearProfileListener();
      const staffRef = doc(firestore, "users", firebaseUser.uid);
      unsubscribeProfile = onSnapshot(
        staffRef,
        (staffSnap) => {
          if (!staffSnap.exists()) {
            if (fallbackToClient) {
              subscribeToClientProfile(firebaseUser, false);
            } else {
              setUnauthorizedProfile(firebaseUser);
            }
            return;
          }

          const data = staffSnap.data();
          const active = data.isActive !== false && String(data.status || "active").toLowerCase() !== "inactive";
          setUserData({
            id: firebaseUser.uid,
            profileType: "staff",
            role: active ? String(data.role || "staff").toLowerCase() : "disabled",
            active,
            name: data.name || data.username || firebaseUser.displayName || data.email || firebaseUser.email?.split("@")[0] || "Staff",
            email: data.email || firebaseUser.email,
            ...data,
          });
          setLoading(false);
        },
        () => {
          // A client is correctly denied access to the internal users collection.
          // For legacy accounts without custom claims, try the client profile next.
          if (fallbackToClient) {
            subscribeToClientProfile(firebaseUser, false);
          } else {
            setUnauthorizedProfile(firebaseUser);
          }
        }
      );
    };

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      authGeneration += 1;
      const generation = authGeneration;
      clearProfileListener();

      if (!firebaseUser) {
        setUser(null);
        setUserData(null);
        setLoading(false);
        loginRecordedFor.current = null;
        return;
      }

      setUser(firebaseUser);
      setUserData(null);
      setLoading(true);

      try {
        const tokenResult = await getIdTokenResult(firebaseUser);
        if (generation !== authGeneration || auth.currentUser?.uid !== firebaseUser.uid) return;

        const claimedProfileType = String(tokenResult.claims?.profileType || "").toLowerCase();
        const claimedRole = String(tokenResult.claims?.role || "").toLowerCase();

        if (claimedProfileType === "client" || claimedRole === "client") {
          subscribeToClientProfile(firebaseUser, false);
          return;
        }

        if (claimedProfileType === "staff" || STAFF_ROLES.includes(claimedRole)) {
          subscribeToStaffProfile(firebaseUser, false);
          return;
        }

        // Backward-compatible fallback for older staff/client Auth accounts that predate custom claims.
        // Staff accounts can read their own users document; client accounts cannot, so a permission
        // denial cleanly falls through to clientUsers without weakening Firestore security rules.
        subscribeToStaffProfile(firebaseUser, true);
      } catch (error) {
        if (generation !== authGeneration || auth.currentUser?.uid !== firebaseUser.uid) return;
        console.warn("Unable to resolve authentication profile claims:", error);
        subscribeToStaffProfile(firebaseUser, true);
      }
    });

    return () => {
      authGeneration += 1;
      clearProfileListener();
      unsubscribeAuth();
    };
  }, []);

  const value = { user, userData };
  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
